import { BodyText, Button, Callout, Separator, Tooltip } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers.ts";
import { trackEvent } from "@/lib/tracking/tracking.ts";
import { useParams } from "@tanstack/react-router";
import { CirclePlus } from "lucide-react";
import { Fragment, useState } from "react";
import type { z } from "zod";
import { SectionContent } from "../components/SectionContent.tsx";
import { SectionHeader } from "../components/SectionHeader.tsx";
import { CareTeamMember } from "./components/CareTeamMember.tsx";
import { CareTeamMemberSkeleton } from "./components/CareTeamMemberSkeleton.tsx";
import { MemberForm } from "./components/MemberForm.tsx";
import {
  useAddExternalCareTeamMember,
  useExternalCareTeam,
} from "./network/useExternalCareTeam.ts";
import type { memberFormSchema } from "./schemas/schemas";

export const ExternalCareTeam = () => {
  const { patientId } = useParams({ strict: false });
  const { data: careTeam, isLoading, isError } = useExternalCareTeam(patientId);
  const [adding, setIsAdding] = useState(false);
  const { mutate: addCareTeamMember, isPending: isSavingAddingMember } =
    useAddExternalCareTeamMember({
      patientId,
      onSuccess: () => setIsAdding(false),
    });

  const onClickAdd = () => {
    if (adding || !patientId) {
      return;
    }
    setIsAdding(true);
  };

  const onSubmitNewMember = async (
    careTeamMember: z.infer<typeof memberFormSchema>,
  ) => {
    if (!patientId) {
      return;
    }

    addCareTeamMember({
      careTeamId: careTeam?.careTeamId,
      members: [
        {
          nameFullFormatted: careTeamMember.name,
          npi: careTeamMember.npi,
          specialtyCode: {
            code: careTeamMember.specialtyCode,
            display: careTeamMember.specialtyCodeDisplay,
            system: careTeamMember.specialtyCodeSystem,
          },
        },
      ],
    });
  };

  const onCancelNewMember = () => {
    trackEvent(TRACKERS.CANCEL_CARE_TEAM);
    setIsAdding(false);
  };

  const isCareTeamEmpty =
    !isLoading &&
    !isError &&
    (!careTeam || !careTeam.members.length) &&
    !adding;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Care Team"
        actionButtons={[
          <Tooltip tooltipContentText="Add" side="bottom" key="add">
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => {
                trackEvent(TRACKERS.ADD_CARE_TEAM_MEMBER_ICON);
                onClickAdd();
              }}
              aria-label="Add external care team member"
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {(isLoading || !patientId) && <CareTeamMemberSkeleton />}
        {isError && (
          <div className="flex relative flex-col gap-2 px-3 py-2 w-full">
            <Callout
              className="w-full"
              variant="danger"
              title="Something went wrong!"
              description="Please refresh and try again."
            />
          </div>
        )}
        {isCareTeamEmpty && (
          <div className="flex gap-1 py-2 w-full">
            <BodyText size="sm" textColor="tertiary">
              No care team members documented
            </BodyText>
          </div>
        )}
        {adding && (
          <div className="mt-1">
            <MemberForm
              onCancel={onCancelNewMember}
              onSubmit={onSubmitNewMember}
              isSaving={isSavingAddingMember}
            />
          </div>
        )}
        {!!patientId &&
          careTeam?.members?.map((participant) => (
            <Fragment key={participant.nameFullFormatted}>
              <CareTeamMember
                key={`member-${participant.nameFullFormatted}`}
                patientId={patientId}
                careTeamId={careTeam.careTeamId}
                careTeamMember={participant}
              />
              <Separator />
            </Fragment>
          ))}
      </SectionContent>
    </div>
  );
};
